import type { ReploSimpleProject } from "schemas/generated/project";
import type { CurrencyCode } from "schemas/project";
import type { ShopSettings } from "../../../../../features/shops/hooks/useUpdateShop";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { Loader } from "@editor/components/common/Loader";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useOpenModal } from "@editor/hooks/useModal";
import { getCurrencySymbol } from "@editor/utils/analytics";

import {
  LoadedSettings,
  SettingsContent,
  SettingsLayout,
} from "@/editor/components/shops-ui/settings/SettingsCard";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { formatInTimeZone } from "date-fns-tz";
import { Clock } from "lucide-react";
import { useForm } from "react-hook-form";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatIanaTimezone } from "replo-utils/lib/timezone";
import { CURRENCIES } from "schemas/project";

import { useUpdateShop } from "../../../../../features/shops/hooks/useUpdateShop";

const ShopSettingsForm: React.FC<{
  project: ReploSimpleProject;
}> = ({ project }) => {
  const { updateShop, isUpdating } = useUpdateShop();
  const {
    handleSubmit,
    setValue,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<ShopSettings>({
    defaultValues: {
      name: project.name ?? "",
      timeZone:
        project.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      currency: project.currency ?? "USD",
    },
  });

  const handleSetValue = (
    key: keyof ShopSettings,
    value: ShopSettings[keyof ShopSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const updateSettingsSections = [
    {
      title: "Shop Name",
      component: (
        <ShopNameInput
          initialName={watch("name") ?? ""}
          onChange={(value) => handleSetValue("name", value)}
        />
      ),
    },
    {
      title: "Timezone",
      component: (
        <ShopTimeZoneSettings
          timeZone={watch("timeZone") ?? ""}
          onChange={(value) => handleSetValue("timeZone", value)}
        />
      ),
    },
    {
      title: "Currency",
      component: (
        <ShopCurrencySettings
          currency={watch("currency") ?? "USD"}
          onChange={(value) => handleSetValue("currency", value)}
        />
      ),
    },
  ];

  const handleShopUpdate = async (shopSettings: ShopSettings) => {
    await updateShop({
      id: project.id,
      data: shopSettings,
    });
    reset({}, { keepValues: true });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {updateSettingsSections.map((section) => {
        return (
          <SettingsContent key={section.title} title={section.title}>
            {section.component}
          </SettingsContent>
        );
      })}
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleSubmit(handleShopUpdate)()}
          isLoading={isUpdating}
          disabled={!isDirty}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const ShopTimeZoneSettings: React.FC<{
  timeZone: string;
  onChange: (value: string) => void;
}> = ({ timeZone, onChange }) => {
  const getTimeZoneLabel = (timezone: string) => {
    if (timezone === "UTC") {
      return "UTC (GMT±00:00)";
    }

    const formattedTimezone = formatIanaTimezone(timezone);

    // NOTE (Kurt, 2025-03-03): We format the GMT part to be more readable by
    // replacing GMT+0:00 or GMT-0:00 with GMT±0:00.
    let gmtPart = formatInTimeZone(new Date(), timezone, "'GMT'xxx");
    gmtPart = gmtPart.replace(/GMT[+-]00:00/g, "GMT±00:00");

    return `${formattedTimezone} (${gmtPart})`;
  };

  const timezoneOptions = Intl.supportedValuesOf("timeZone").map(
    (timezone) => ({
      label: getTimeZoneLabel(timezone),
      value: timezone,
    }),
  );

  timezoneOptions.unshift({
    label: getTimeZoneLabel("UTC"),
    value: "UTC",
  });

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={timezoneOptions}
        value={timeZone ?? undefined}
        onChange={(value) => onChange(value)}
        trigger={
          <Combobox.SelectionButton
            title={getTimeZoneLabel(timeZone ?? "")}
            titleAlignment="start"
            isPlaceholder={!Boolean(timeZone)}
            startEnhancer={<Clock size={14} />}
          />
        }
        areOptionsSearchable
      />
      <div className="text-muted text-sm">
        All analytics will be reported using this timezone.
      </div>
    </div>
  );
};

const ShopCurrencySettings: React.FC<{
  currency: CurrencyCode;
  onChange: (value: string) => void;
}> = ({ currency, onChange }) => {
  const formatCurrencyLabel = (code: CurrencyCode, name: string) => {
    const symbol = getCurrencySymbol(code);
    // NOTE (Kurt, 2025-04-02): Some currency symbols aren't rendered consistently in the browser, so the
    // Intl API returns the currency code instead, if we get a code back we don't want to show it since we
    // already show the currency code in the label.
    return `${code} - ${name}${symbol !== code ? ` (${symbol})` : ""}`;
  };

  const currencyOptions = Object.entries(CURRENCIES).map(
    ([code, { name }]) => ({
      label: formatCurrencyLabel(code as CurrencyCode, name),
      value: code,
    }),
  );

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={currencyOptions}
        value={currency}
        onChange={(value) => onChange(value)}
        trigger={
          <Combobox.SelectionButton
            title={formatCurrencyLabel(currency, CURRENCIES[currency].name)}
            titleAlignment="start"
            isPlaceholder={!Boolean(currency)}
          />
        }
        areOptionsSearchable
      />
      <div className="text-muted text-sm">
        All analytics will be reported in this currency.
      </div>
    </div>
  );
};

const ShopNameInput = ({
  initialName,
  onChange,
}: {
  initialName: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Input
        value={initialName}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your shop name"
        size="base"
      />
    </div>
  );
};

type ShopSectionProps = {
  project: ReploSimpleProject;
};

const ShopMoveSection: React.FC<ShopSectionProps & { disabled: boolean }> = ({
  project,
  disabled,
}) => {
  const openModal = useOpenModal();

  return (
    <div className="flex w-full flex-row justify-end">
      <Button
        variant="primary"
        size="base"
        disabled={disabled}
        onClick={() => {
          if (project.ownerWorkspace?.id) {
            openModal({
              type: "moveProjectModal",
              props: {
                projectId: project.id,
                oldWorkspaceId: project?.ownerWorkspace?.id,
                shopifyUrl: project.integrations?.shopify?.store.shopifyUrl,
              },
            });
          }
        }}
      >
        Move Shop
      </Button>
    </div>
  );
};

const ShopDeleteSection: React.FC<ShopSectionProps & { disabled: boolean }> = ({
  project,
  disabled,
}) => {
  const openModal = useOpenModal();

  return (
    <div className="flex flex-col gap-4">
      <InlineAlert variant="error" multiline>
        Deleting this shop will erase it. You will not be able to recover this
        Shop without the help of the Replo support team.
      </InlineAlert>
      <div className="flex w-full flex-row justify-end">
        <Button
          variant="danger"
          size="base"
          disabled={disabled}
          onClick={() => {
            openModal({
              type: "deleteProjectModal",
              props: {
                projectId: project.id,
                from: "list",
              },
            });
          }}
        >
          Delete Shop
        </Button>
      </div>
    </div>
  );
};

const ShopSettingsWrapper = () => {
  const { project, isLoading: isLoadingProject } = useCurrentProjectContext();
  const { userRole } = useCurrentProjectContext();
  const canDeleteOrMoveShop = exhaustiveSwitch({ type: userRole ?? "none" })({
    superuser: () => true,
    workspaceOwner: () => true,
    workspaceMember: () => false,
    projectMember: () => false,
    none: () => false,
  });

  if (!project) {
    return null;
  }

  const sections = [
    {
      title: "General Shop Settings",
      component: <ShopSettingsForm project={project} />,
    },
    ...(canDeleteOrMoveShop
      ? [
          {
            title: "Move Shop",
            description: "Move this shop to a different workspace.",
            component: <ShopMoveSection project={project} disabled={false} />,
          },
          {
            title: "Delete Shop",
            component: <ShopDeleteSection project={project} disabled={false} />,
          },
        ]
      : []),
  ];

  return (
    <SettingsLayout>
      {isLoadingProject ? <Loader /> : <LoadedSettings sections={sections} />}
    </SettingsLayout>
  );
};

export default ShopSettingsWrapper;
